import { useState, useMemo } from 'react';
import Head from 'next/head';
import { DeviceCard } from '../components/DeviceCard';
import { FleetStats } from '../components/FleetStats';
import { AnomalyDisplay } from '../components/AnomalyDisplay';
import { ImmediateAttention } from '../components/ImmediateAttention';
import { useAnomalies } from '../contexts/AnomaliesContext';
import { useSettings } from '../contexts/SettingsContext';
import { useDeviceContext } from '../contexts/DeviceContext';
import { useTelemetryContext } from '../contexts/TelemetryContext';
import { _correlateDevicesByFarmHash } from '../contexts/DeviceContext';
import { Device } from '../types';

export default function Dashboard() {
  const { settings } = useSettings();
  const {
    devices: allDevices,
    loading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
    getProductionDevices,
    triggerDiscovery,
    pendingReviewDevices,
  } = useDeviceContext();
  const { anomalies, loading: anomaliesLoading } = useAnomalies();
  const { barnTemperature: barnTemperatureData } = useTelemetryContext();
  const pendingReviewCount = pendingReviewDevices?.length || 0;

  // Only show skeletons on initial load (no data yet), not on background refreshes
  const isInitialLoad = devicesLoading && !allDevices;
  const isAnomaliesInitialLoad = anomaliesLoading && !anomalies;

  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');

  // Get production devices (Modberry whitelist only, active, non-experimental)
  // Sort: commissioned devices first, uncommissioned to bottom
  const productionDevices = useMemo(() => {
    const devices = getProductionDevices();
    return [...devices].sort((a, b) => {
      const aCommissioned = a.commissionedAt ? 0 : 1;
      const bCommissioned = b.commissionedAt ? 0 : 1;
      return aCommissioned - bCommissioned;
    });
  }, [getProductionDevices]);

  // Correlate barn temperatures with devices by farm hash
  const deviceTemperatureMap = useMemo(() => {
    const tempMap = new Map<string, Array<{ deviceId: string; deviceName: string; temperature: number; timestamp: string }>>();

    if (!barnTemperatureData || barnTemperatureData.length === 0 || !allDevices) {
      return tempMap;
    }

    const correlationMap = _correlateDevicesByFarmHash(allDevices);

    // For each deployment, find correlated thermometers
    correlationMap.forEach((correlation) => {
      if (!correlation.flowDeviceId) return;

      const thermometers: Array<{ deviceId: string; deviceName: string; temperature: number; timestamp: string }> = [];

      // Find temperature data for each correlated thermometer
      correlation.barnThermometerDeviceIds.forEach((thermId) => {
        const thermData = barnTemperatureData.find((t) => t.deviceId === thermId);

        if (thermData && thermData.timeSeries && thermData.timeSeries.length > 0) {
          // Get most recent temperature
          const latest = thermData.timeSeries[0];
          thermometers.push({
            deviceId: thermData.deviceId,
            deviceName: thermData.deviceName,
            temperature: latest.temperature,
            timestamp: latest.timestamp
          });
        }
      });

      if (thermometers.length > 0) {
        tempMap.set(correlation.flowDeviceId, thermometers);
      }
    });

    return tempMap;
  }, [barnTemperatureData, allDevices]);

  // Calculate fleet stats from production devices only (Modberry-filtered)
  const fleetStats = useMemo(() => {
    if (!productionDevices || productionDevices.length === 0) return null;

    // All stats should be based on production Modberry devices only
    const activeDevices = productionDevices.filter((d: { isActive?: boolean }) => d.isActive);

    const healthyDevices = productionDevices.filter((d: { telemetryHealthScore?: number }) =>
      (d.telemetryHealthScore || 0) >= 80
    );

    const warningDevices = productionDevices.filter((d: { telemetryHealthScore?: number }) =>
      (d.telemetryHealthScore || 0) >= 60 && (d.telemetryHealthScore || 0) < 80
    );

    const criticalDevices = productionDevices.filter((d: { telemetryHealthScore?: number }) =>
      (d.telemetryHealthScore || 0) < 60
    );

    // Count offline commissioned devices only (uncommissioned devices are expected to be offline)
    const offlineDevices = productionDevices.filter((d: Device) =>
      d.metrics?.status === 'offline' && d.commissionedAt
    );

    // Calculate average health score from production devices
    const healthScores = productionDevices
      .map((d: { telemetryHealthScore?: number }) => d.telemetryHealthScore)
      .filter((score: number | undefined): score is number => typeof score === 'number' && score > 0);

    const avgHealthScore = healthScores.length > 0
      ? Math.round(healthScores.reduce((sum: number, score: number) => sum + score, 0) / healthScores.length)
      : 0;

    return {
      total_devices: productionDevices.length,
      active_devices: activeDevices.length,
      healthy_devices: healthyDevices.length,
      warning_devices: warningDevices.length,
      critical_devices: criticalDevices.length,
      offline_devices: offlineDevices.length,
      auto_discovered_devices: productionDevices.length, // All production devices are auto-discovered
      devices_needing_review: pendingReviewCount,
      avg_health_score: avgHealthScore,
    };
  }, [productionDevices, pendingReviewCount]);

  // Filter anomalies by severity and limit from settings
  const filteredAnomalies = useMemo(() => {
    if (!anomalies) return [];
    
    // First filter by severity if selected
    let filtered = anomalies;
    if (selectedSeverity) {
      filtered = anomalies.filter(anomaly => 
        anomaly.severity.toLowerCase() === selectedSeverity.toLowerCase()
      );
    }
    
    // Then limit the results
    return filtered.slice(0, settings.anomaliesLimit || 15);
  }, [anomalies, selectedSeverity, settings.anomaliesLimit]);

  const handleDiscovery = async () => {
    setDiscoveryLoading(true);
    try {
      const success = await triggerDiscovery();
      if (success) {
        // Refresh data after discovery
        refetchDevices();
      }
    } catch (error) {
      console.error('Discovery failed:', error);
    } finally {
      setDiscoveryLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>Struvital Monitoring Dashboard</title>
        <meta name="description" content="Struvital Mission Control - System Overview" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.svg" />
      </Head>
      
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Struvital Monitoring Dashboard
              </h1>
              <p className="text-gray-600 mt-1">
                Struvital Mission Control
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleDiscovery}
                disabled={discoveryLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {discoveryLoading ? 'Scanning...' : '🔍 Discover Systems'}
              </button>
              <div className="flex items-center px-3 py-2 bg-green-100 text-green-800 rounded-lg">
                <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                System Online
              </div>
            </div>
          </div>
        </div>

        <main>
          {/* Fleet Statistics */}
          {isInitialLoad ? (
            <div className="animate-pulse mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="rounded-lg border-2 border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-8 w-8 bg-gray-200 rounded"></div>
                      <div className="h-8 w-12 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border-2 border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-40"></div>
                    <div className="flex items-center space-x-4">
                      <div className="h-8 w-16 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded w-48"></div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : fleetStats ? (
            <FleetStats stats={fleetStats} />
          ) : null}

          {/* Immediate Attention - Critical items requiring urgent intervention */}
          {isInitialLoad || isAnomaliesInitialLoad ? (
            <div className="animate-pulse bg-gray-50 border-l-4 border-gray-300 rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  <div className="space-y-1">
                    <div className="h-5 bg-gray-200 rounded w-56"></div>
                    <div className="h-4 bg-gray-200 rounded w-36"></div>
                  </div>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <div className="h-6 w-6 bg-gray-200 rounded"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <ImmediateAttention anomalies={anomalies} devices={allDevices} />
          )}

          {/* Recent Anomalies */}
          {isAnomaliesInitialLoad ? (
            <div className="bg-white rounded-lg shadow mb-8 animate-pulse">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="h-6 bg-gray-200 rounded w-44"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 bg-gray-200 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                    </div>
                    <div className="h-4 w-16 bg-gray-200 rounded flex-shrink-0"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : ((filteredAnomalies && filteredAnomalies.length > 0) || (anomalies && anomalies.length > 0)) ? (
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Recent Anomalies</h2>
                  <div className="flex items-center gap-4">
                    {/* Severity Filter */}
                    <div className="flex items-center gap-2">
                      <label htmlFor="severity-filter" className="text-sm text-gray-600">
                        Filter:
                      </label>
                      <select
                        id="severity-filter"
                        value={selectedSeverity}
                        onChange={(e) => setSelectedSeverity(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All severities</option>
                        <option value="critical">🚨 Critical</option>
                        <option value="high">⚠️ High</option>
                        <option value="medium">⚡ Medium</option>
                        <option value="low">ℹ️ Low</option>
                      </select>
                    </div>
                    <span className="text-sm text-gray-600">
                      {filteredAnomalies.length} of {anomalies ? anomalies.length : 0} issues
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {filteredAnomalies.length > 0 ? (
                  <AnomalyDisplay 
                    anomalies={filteredAnomalies.map((anomaly: { id: string; farmName?: string; deviceId: string; description: string; severity: string; detectedAt: string; acknowledged?: boolean }) => ({
                      id: anomaly.id,
                      type: 'System Alert',
                      description: anomaly.description,
                      severity: anomaly.severity,
                      timestamp: anomaly.detectedAt,
                      deviceId: anomaly.deviceId,
                      farmName: anomaly.farmName,
                      acknowledged: anomaly.acknowledged
                    }))}
                    maxItems={settings.anomaliesLimit || 25}
                    showDeviceLink={true}
                    compact={true}
                  />
                ) : selectedSeverity ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">🔍</div>
                    <p className="text-gray-600">No {selectedSeverity} severity anomalies found</p>
                    <button
                      onClick={() => setSelectedSeverity('')}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      Show all anomalies
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">✅</div>
                    <p className="text-gray-600">No recent anomalies detected</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Main Content - Devices */}
          <div className="mb-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Production Systems</h2>
                <div className="text-sm text-gray-600">
                  {productionDevices ? `${productionDevices.length} systems` : '--'}
                </div>
              </div>

              {isInitialLoad ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                    <div className="animate-pulse">
                      {/* Header: farm name + status badges */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="h-5 bg-gray-200 rounded w-2/3"></div>
                        <div className="space-y-1">
                          <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
                          <div className="h-5 w-20 bg-gray-200 rounded-full"></div>
                        </div>
                      </div>
                      {/* Barn temperature section */}
                      <div className="mb-4 pt-3 border-t border-gray-200">
                        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="h-6 bg-gray-100 rounded w-full"></div>
                      </div>
                      {/* Flow rates */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="flex justify-between">
                          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        </div>
                        <div className="flex justify-between">
                          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        </div>
                      </div>
                      {/* Health + last updated */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/6"></div>
                        </div>
                        <div className="flex justify-between">
                          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/5"></div>
                        </div>
                      </div>
                      {/* Footer: active + links */}
                      <div className="flex justify-between items-center">
                        <div className="h-3 bg-gray-200 rounded w-1/5"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              ) : devicesError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                  <p className="text-red-800 font-medium">Error loading devices</p>
                  <p className="text-red-600 text-sm mt-1">{devicesError}</p>
                  <button 
                    onClick={() => refetchDevices()}
                    className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium"
                  >
                    Retry
                  </button>
                </div>
              ) : productionDevices && productionDevices.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {productionDevices.map((device) => (
                  <DeviceCard
                    key={device.deviceId}
                    device={device}
                    metrics={device.metrics}
                    barnTemperatures={deviceTemperatureMap.get(device.deviceId)}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <div className="text-4xl mb-4">🔍</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Systems Found</h3>
                <p className="text-gray-600 mb-4">
                  Click &quot;Discover Systems&quot; to automatically find production systems.
                </p>
                <button
                  onClick={handleDiscovery}
                  disabled={discoveryLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium"
                >
                  {discoveryLoading ? 'Scanning...' : 'Start Discovery'}
                </button>
                </div>
              )}
          </div>
        </main>
      </div>
    </>
  );
}